import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "src/api";
import { useMerchflowsApi } from "src/api/merchflows";
import { MultiFileUploader, Tooltip } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import { useModals } from "src/components/Modals";
import { useAsyncState } from "src/hooks/useAsyncState";
import { Button, Color, Flex, Icon, Modal, Select, SubgridTable, Text } from "src/elements";
import { downloadFileFromUrl } from "src/utils/downloadFile";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { routeToUrl } from "@CommonUtils/routes";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";
import {
  ResponsePostMerchflowsImportPlanogramsFromPsa,
  RoutePostMerchflowsImportPlanogramsFromPsa,
} from "@CommonApi/merchflows/import-planograms-from-psa";
import {
  ImportPlanogramsFromPsaModalData,
  importPlanogramsFromPsaModalDataAtom,
} from "./store/atoms";
import { queryClient } from "src/utils/queryClient";
import { STUDIO_ATTRIBUTES_QUERY_ROOT } from "src/pages/CdtStudio/store/useStudioAttributesQuery";
import { AisleSplitSuggestions } from "src/pages/PsaRenderer/components/AisleSplitSuggestions";
import { useTranslation } from "src/i18n";

type StoreResult = ResponsePostMerchflowsImportPlanogramsFromPsa["store_results"][0];

const aisleBreakTargetOf = ({ store_id, category_id }: StoreResult) =>
  store_id !== null && category_id !== null
    ? { storeId: store_id, categoryId: category_id }
    : undefined;

export const aisleBreaksColumn = (header: string): SubgridColumn<StoreResult> => ({
  id: "aisle_break_suggestions",
  header,
  justify: "left",
  renderer: ({ row }) => (
    <AisleSplitSuggestions
      suggestions={row.aisle_break_suggestions ?? []}
      target={aisleBreakTargetOf(row)}
    />
  ),
});

const buildColumns = (
  onDownloadSyncLog: (storeCode: string) => void,
): SubgridColumn<StoreResult>[] => [
  {
    id: "file_name",
    header: "File",
    justify: "left",
  },
  {
    id: "store_code",
    header: "Store Code",
    justify: "left",
  },
  {
    id: "status",
    header: "Status",
    justify: "center",
    renderer: ({ value, row }) => {
      const isSuccess = value === "SUCCESS";
      const productSync = row.product_sync;
      const hasProductSync =
        isSuccess &&
        productSync &&
        (productSync.products_not_found > 0 ||
          productSync.products_updated > 0 ||
          productSync.fields_updated > 0);

      return (
        <Flex align="center" gap="5px">
          <Icon
            name={isSuccess ? "checkmarkCircle" : "clearCircle"}
            color={isSuccess ? Color.primary : Color.red}
            size={14}
          />
          <Text variant="small2">{value}</Text>
          {hasProductSync && productSync && (
            <Flex onClick={() => onDownloadSyncLog(row.store_code)} cursor="pointer">
              <Icon name="warningCircle" color={Color.yellow} size={14} />
              <Tooltip>
                {[
                  productSync.products_not_found > 0 &&
                    `${productSync.products_not_found} products not found in DB`,
                  productSync.products_updated > 0 &&
                    `${productSync.products_updated} products updated`,
                  productSync.fields_updated > 0 && `${productSync.fields_updated} fields updated`,
                ]
                  .filter(Boolean)
                  .join(", ") + ". Click to download report."}
              </Tooltip>
            </Flex>
          )}
        </Flex>
      );
    },
  },
  {
    id: "error",
    header: "Error",
    justify: "left",
    renderer: ({ value }) => (
      <Text variant="small2" color={value ? Color.red : Color.textSecondary}>
        {value || "-"}
      </Text>
    ),
  },
];

export const ModalImportPlanogramsFromPsa = () => {
  const [data, setData] = useAtom(importPlanogramsFromPsaModalDataAtom);

  useEffect(() => {
    return () => {
      setData(null);
    };
  }, [setData]);

  if (!data) {
    return null;
  }

  return <ModalImportPlanogramsFromPsaInner flowData={data} />;
};

interface Props {
  flowData: ImportPlanogramsFromPsaModalData;
}

const ModalImportPlanogramsFromPsaInner = ({ flowData }: Props) => {
  const { hasLoader } = useLoaders();
  const { closeModal } = useModals();
  const { navigate } = useNavigation();
  const { postMerchflowImportPlanogramsFromPsaApi } = useMerchflowsApi();
  const { getMerchflowStoreProductSyncLogDownloadUrlApi } = useApi();
  const { t } = useTranslation("common");

  const [category, setCategory] = useState<string | null>(flowData?.category || null);
  const [subcategory, setSubcategory] = useState<string | null>(flowData?.subcategory || null);
  const [psaFiles, setPsaFiles] = useState<File[]>([]);
  const [fileUploaderKey, setFileUploaderKey] = useState(0);
  const [response, setResponse] =
    useAsyncState<ResponsePostMerchflowsImportPlanogramsFromPsa | null>(null);

  const isImportLoading = hasLoader(RoutePostMerchflowsImportPlanogramsFromPsa);

  const categories = useMemo(() => {
    if (!flowData) return [];
    return Object.keys(flowData.availableCategories).sort();
  }, [flowData]);

  const subcategories = useMemo(() => {
    if (!flowData || !category) return [];
    return [...(flowData.availableCategories[category] || [])].sort();
  }, [flowData, category]);

  useEffect(() => {
    if (!category && categories.length > 0) {
      setCategory(categories[0]);
    }
  }, [category, categories]);

  useEffect(() => {
    if (!subcategory && subcategories.length > 0) {
      setSubcategory(subcategories[0]);
      return;
    }

    if (subcategory && !subcategories.includes(subcategory)) {
      setSubcategory(subcategories[0] || null);
    }
  }, [subcategory, subcategories]);

  const importPlanogramsFromPsa = async () => {
    if (!category || !subcategory || psaFiles.length === 0) return;

    const importResponse = await postMerchflowImportPlanogramsFromPsaApi({
      psaFiles,
      merchflowName: flowData.name,
      goLiveDate: flowData.goLiveDate.toISOString(),
      coreRangeOption: flowData.coreRange,
      notes: flowData.notes || "",
      ...(flowData.deactivationDate && {
        deactivationDate: flowData.deactivationDate.toISOString(),
      }),
      retailerCategoryCode: category,
      categoryCode: subcategory,
    });

    setResponse(importResponse);

    // Root key: this modal has categoryCode, the cache is keyed by category id.
    if (importResponse && importResponse.successful_stores > 0) {
      void queryClient.invalidateQueries({ queryKey: [STUDIO_ATTRIBUTES_QUERY_ROOT] });
    }

    if (importResponse && importResponse.successful_stores === 0) {
      setPsaFiles([]);
      setFileUploaderKey((k) => k + 1);
    }
  };

  const handleViewStores = () => {
    if (!response || !response.merchflow_id) return;
    closeModal("ModalImportPlanogramsFromPsa");
    closeModal("ModalMerchflowReview");
    navigate(
      routeToUrl(pages.merchflows_merchflowId_StoresAll, {
        merchflowId: response.merchflow_id,
      }),
    );
  };

  const handleDownloadSyncLog = useCallback(
    async (storeCode: string) => {
      if (!response?.merchflow_id) return;
      const result = await getMerchflowStoreProductSyncLogDownloadUrlApi(
        response.merchflow_id,
        storeCode,
      );
      if (result?.url) {
        await downloadFileFromUrl(result.url);
      }
    },
    [response?.merchflow_id, getMerchflowStoreProductSyncLogDownloadUrlApi],
  );

  const hasAisleBreakSuggestions = Boolean(
    response?.store_results.some((row) => (row.aisle_break_suggestions ?? []).length > 0),
  );
  const columns = useMemo(
    () => [
      ...buildColumns(handleDownloadSyncLog),
      ...(hasAisleBreakSuggestions
        ? [aisleBreaksColumn(t("aisleBreaks.result.columnHeader"))]
        : []),
    ],
    [handleDownloadSyncLog, hasAisleBreakSuggestions, t],
  );
  const hasSuccessfulStores = Boolean(response && response.successful_stores > 0);

  return (
    <Modal
      name="ModalImportPlanogramsFromPsa"
      title="Import PSA for New Flow"
      width={hasAisleBreakSuggestions ? "1000px" : "700px"}
    >
      <Flex padding="10px" column gap="16px" flexGrow minHeight="0" overflow="hidden">
        <Flex gap="10px">
          <MultiFileUploader
            key={fileUploaderKey}
            onChange={(files) => {
              setPsaFiles(files);
              if (files.length > 0) setResponse(null);
            }}
            isLoading={isImportLoading}
            onSuccess={
              hasSuccessfulStores
                ? response?.failed_stores === 0
                  ? "Import completed"
                  : "Import finished with errors"
                : undefined
            }
          />
        </Flex>

        <Flex gap="10px">
          <Select
            width="100%"
            title="Category"
            value={category}
            setValue={setCategory}
            placeholder="Category"
            options={categories.map((value) => ({ label: value, value }))}
            isRequired
            isSearchable
            isDisabled={isImportLoading || hasSuccessfulStores}
          />

          <Select
            width="100%"
            title="Sub-Category"
            value={subcategory}
            setValue={setSubcategory}
            placeholder="Sub-Category"
            options={subcategories.map((value) => ({ label: value, value }))}
            isRequired
            isSearchable
            isDisabled={isImportLoading || hasSuccessfulStores || !category}
          />
        </Flex>

        {response && (
          <Flex column flexGrow minHeight="0" overflow="auto" gap="10px">
            <Flex gap="10px" align="center">
              <Text variant="body2">
                Total: {response.total_stores} | Success: {response.successful_stores} | Failed:{" "}
                {response.failed_stores}
              </Text>
            </Flex>
            {!hasSuccessfulStores && (
              <Text variant="small2" color={Color.red}>
                Import finished, but no stores were imported successfully. Please try again.
              </Text>
            )}
            <SubgridTable columns={columns} data={response.store_results} />
          </Flex>
        )}

        <Flex gap="10px" justify="center">
          <Button
            isDisabled={
              hasSuccessfulStores
                ? false
                : psaFiles.length === 0 || !category || !subcategory || isImportLoading
            }
            onClick={hasSuccessfulStores ? handleViewStores : importPlanogramsFromPsa}
          >
            {hasSuccessfulStores ? "View Stores" : "Import"}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
